import { ChevronsUpDown, Filter, type LucideIcon } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../lib/utils';

/** Column-header chrome icon discriminator. */
export type ColumnHeaderIconKind = 'sortCaret' | 'filterFunnel';

export interface ColumnHeaderIconProps {
  kind: ColumnHeaderIconKind;
  className?: string;
  /** Optional aria-label. When omitted, the glyph is decorative (`aria-hidden`). */
  'aria-label'?: string;
}

const KIND_TO_GLYPH = {
  sortCaret: ChevronsUpDown,
  filterFunnel: Filter,
} as const satisfies Readonly<Record<ColumnHeaderIconKind, LucideIcon>>;

/**
 * ColumnHeaderIcon — sort caret / filter funnel @ 10px per Figma registry
 * (design.md § 3.2). Sits in column-header chrome to indicate sortability or
 * an active column filter.
 */
export const ColumnHeaderIcon: React.FC<ColumnHeaderIconProps> = ({
  kind,
  className,
  'aria-label': ariaLabel,
}) => {
  const Glyph = KIND_TO_GLYPH[kind];
  return (
    <Glyph
      width={10}
      height={10}
      className={cn(className)}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
};

ColumnHeaderIcon.displayName = 'ColumnHeaderIcon';
